import * as Lucide from 'lucide-react'
import { Check, ChevronDown, ChevronRight, Circle, icons, Pencil, Pin, PinOff, Plus, Search, Wand2 } from 'lucide-react'
import { useEffect, useMemo, useState, type ComponentType, type CSSProperties, type KeyboardEvent } from 'react'
import { create } from 'zustand'

import { useKindMutations } from '@/api/vocab'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toast } from '@/components/ui/toast'
import { cn } from '@/components/ui/utils'
import { enterScope } from '@/lib/keys'
import { useEditorStore } from '@/stores/editorStore'
import { useKindPrefs } from '@/stores/vocabStore'
import type { KindDef } from '@/schema'

import { changedAnything, type ConvertOutcome, type SkipReason } from './convert'
import { NewKindDialog } from './NewKindDialog'
import { TOOL_BY_ID } from './tools/reducer'
import {
  addPortal,
  currentVocab,
  describeKind,
  digitShortcuts,
  DOMAIN_WORDS,
  domainOfFeature,
  domainOfTool,
  featuresOf,
  groupKinds,
  kindColour,
  kindCounts,
  kindLabel,
  matchesQuery,
  prettyId,
  setKindOf,
  SLOT_OF,
  toolOfDomain,
  useVocab,
  type StageDomain,
  type VocabLike,
} from './vocab'

/* -------------------------------------------------------------------------- */
/* Icons from the vocabulary                                                   */
/* -------------------------------------------------------------------------- */

export type IconComponent = ComponentType<{ className?: string; style?: CSSProperties; 'aria-hidden'?: boolean }>

/** Canonical icons (the picker's list). */
const ICONS = icons as unknown as Record<string, IconComponent>
/** Every export, including the old names lucide still aliases (`Waves`, `CircleHelp`) that the vocabulary uses. */
const ALL = Lucide as unknown as Record<string, IconComponent | undefined>
let lowerIndex: Map<string, IconComponent> | null = null

/**
 * A lucide icon by the name the vocabulary stores — kebab-case (`door-open`, `grid-2x2`), with
 * PascalCase (`DoorOpen`) accepted too. `null` for a name lucide does not have.
 */
export function lucideIcon(name: string | undefined | null): IconComponent | null {
  if (!name) return null
  const direct = ICONS[name]
  if (direct) return direct
  if (!lowerIndex) {
    lowerIndex = new Map()
    for (const [k, v] of Object.entries(ALL)) if (v && /^[A-Z]/.test(k) && typeof v === 'object') lowerIndex.set(k.toLowerCase(), v)
    for (const [k, v] of Object.entries(ICONS)) lowerIndex.set(k.toLowerCase(), v)
  }
  return lowerIndex.get(name.replace(/[-_\s]/g, '').toLowerCase()) ?? null
}

/** A kind's icon, or a plain circle when it has none (or one lucide does not know). */
export function KindIcon({ name, className, style }: { name?: string | null; className?: string; style?: CSSProperties }) {
  const Icon = lucideIcon(name) ?? (Circle as unknown as IconComponent)
  return <Icon className={className} style={style} aria-hidden />
}

let allNames: string[] | null = null

/** Every lucide icon as a kebab-ish name the vocabulary accepts (`door-open`, `grid2x2`). */
export function iconNames(): string[] {
  if (!allNames) allNames = Object.keys(ICONS).map((n) => n.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase())
  return allNames
}

/* -------------------------------------------------------------------------- */
/* Which kind dialog is open (one host, many triggers)                          */
/* -------------------------------------------------------------------------- */

/**
 * What choosing a kind does:
 *  - `arm`: whatever of that list is selected changes to it; with none selected, the next one
 *    added gets it (and the matching tool is picked);
 *  - `set`: exactly these features change to it;
 *  - `add`: start adding one — pick the drawing tool, or add a way in/out straight away;
 *  - `convert`: these features become one of the picker's list (a shape → a spot, a spot → a
 *    shape or a way in/out, a way in/out → a spot) of the kind picked — one undo step.
 */
export type PickPurpose = { kind: 'arm' } | { kind: 'set'; ids: string[] } | { kind: 'add' } | { kind: 'convert'; ids: string[] }

type EditorDialog = { mode: 'new'; domain: StageDomain; name?: string; purpose: PickPurpose } | { mode: 'edit'; domain: StageDomain; id: string }

interface KindUiState {
  picker: { domain: StageDomain; purpose: PickPurpose } | null
  editor: EditorDialog | null
  openPicker(domain: StageDomain, purpose: PickPurpose): void
  closePicker(): void
  openNewKind(domain: StageDomain, purpose: PickPurpose, name?: string): void
  openEditKind(domain: StageDomain, id: string): void
  closeEditor(): void
}

/** What had the focus when the picker (or "New …" from outside it) was opened: a row's "…" item, a Details button, "Add". */
let pickerOpener: HTMLElement | null = null

const focused = (): HTMLElement | null => {
  const el = typeof document !== 'undefined' ? document.activeElement : null
  return el instanceof HTMLElement && el !== document.body ? el : null
}

/** The annotator's kind dialogs; any panel opens them, `<KindDialogs/>` renders them. */
export const useKindUi = create<KindUiState>()((set) => ({
  picker: null,
  editor: null,
  openPicker: (domain, purpose) => {
    pickerOpener = focused()
    set({ picker: { domain, purpose } })
  },
  closePicker: () => set({ picker: null }),
  openNewKind: (domain, purpose, name) => {
    // From inside the picker the picker's opener still counts; from anywhere else, this is the opener.
    const el = focused()
    if (!el?.closest('[data-testid="kind-picker-dialog"]')) pickerOpener = el
    set({ picker: null, editor: { mode: 'new', domain, purpose, ...(name ? { name } : {}) } })
  },
  openEditKind: (domain, id) => set({ editor: { mode: 'edit', domain, id } }),
  closeEditor: () => set({ editor: null }),
}))

/** Do what choosing `id` means for `purpose` (see {@link PickPurpose}). */
export function performPick(domain: StageDomain, id: string, purpose: PickPurpose, vocab: VocabLike = currentVocab()): void {
  useKindPrefs.getState().pushRecent(domain, id)
  const ed = useEditorStore.getState()
  const label = kindLabel(vocab, id, domain)
  switch (purpose.kind) {
    case 'arm': {
      ed.setActiveKind(SLOT_OF[domain], id)
      // Whatever of this list is selected becomes it; with nothing of it selected, get ready to add one.
      if (setKindOf(ed.selection.ids, id, domain) === 0 && !ed.selection.ids.some((sid) => domainOfFeature(ed.doc, sid) === domain)) {
        if (domainOfTool(ed.tool) !== domain) ed.setTool(toolOfDomain(domain))
      }
      return
    }
    case 'set': {
      setKindOf(purpose.ids, id, domain)
      return
    }
    case 'convert': {
      reportConversion(ed.convertFeatures(purpose.ids, domain, id), domain, label)
      return
    }
    case 'add': {
      ed.setActiveKind(SLOT_OF[domain], id)
      // A way in/out with a spot selected goes straight onto that spot (a door on the door).
      const spot = domain === 'portal_kind' ? ed.selection.ids.find((sid) => domainOfFeature(ed.doc, sid) === 'anchor_kind') : undefined
      if (spot) {
        addPortal(id, { anchorId: spot })
        toast.success(`Added ${label}`, { description: 'Change where it is under “Where is it?” in Details.' })
        return
      }
      const tool = toolOfDomain(domain)
      ed.clearSelection()
      ed.setTool(tool)
      toast.info(`Now add the ${label.toLowerCase()}`, { description: TOOL_BY_ID[tool].hint })
      return
    }
  }
}

const SKIP_WORDS: Record<SkipReason, string> = {
  no_conversion: 'it cannot become one',
  layer_source: 'a walk-behind layer is cut from it',
  missing: 'it is not on the picture any more',
}

const EDGE_WORDS: Record<string, string> = { left: 'left edge', right: 'right edge', top: 'top edge', bottom: 'bottom edge' }

/** Say what a conversion did (and what it could not do), in the words the panels use. */
export function reportConversion(out: ConvertOutcome, domain: StageDomain, label: string): void {
  const w = DOMAIN_WORDS[domain]
  const done = out.selected.length
  if (done && !changedAnything(out)) {
    // Nothing to undo: do not say "Press Undo" (it would undo the edit before this one).
    toast.info(done === 1 ? `Already a ${w.one}: ${label}` : `Already ${w.many}: ${label}`, { description: 'Nothing changed.' })
  } else if (done) {
    const rehomed = out.rehomed.length
      ? `${out.rehomed.length === 1 ? 'The way in or out that stood on it now uses the' : `${out.rehomed.length} ways in and out that stood on them now use the nearest`} ${out.rehomed.length === 1 ? EDGE_WORDS[out.rehomed[0]!.edge] : 'edge'}. `
      : ''
    toast.success(done === 1 ? `Now a ${w.one}: ${label}` : `${done} are now ${w.many}: ${label}`, {
      description: `${rehomed}Press Undo to change ${done === 1 ? 'it' : 'them'} back. Nothing is saved until you press Save.`,
    })
  }
  if (out.skipped.length) {
    const why = [...new Set(out.skipped.map((s) => SKIP_WORDS[s.reason]))].join('; ')
    toast.warning(`${out.skipped.length === 1 ? 'One was' : `${out.skipped.length} were`} left as ${out.skipped.length === 1 ? 'it was' : 'they were'}`, { description: `Not a ${w.one}: ${why}.` })
  }
}

/* -------------------------------------------------------------------------- */
/* The searchable, grouped kind list                                            */
/* -------------------------------------------------------------------------- */

export interface KindPickerProps {
  domain: StageDomain
  /** The kind the thing already is (ticked). */
  current?: string
  onPick(kind: KindDef): void
  /** "+ New …" — with the search text as the suggested name. */
  onNew?(name: string): void
  onEdit?(kind: KindDef): void
  autoFocus?: boolean
  className?: string
}

interface Option {
  optId: string
  kind: KindDef
}

/**
 * Every kind of one list (shapes, spots, ways in and out or paths), searchable and grouped by
 * what it does: Pinned and Recent first, then what is already on this picture, then one group per
 * meaning. Arrow keys move, Enter picks, and the pin puts a kind on the number keys.
 */
export function KindPicker({ domain, current, onPick, onNew, onEdit, autoFocus, className }: KindPickerProps) {
  const vocab = useVocab()
  const doc = useEditorStore((s) => s.doc)
  const pins = useKindPrefs((s) => s.pins[domain])
  const recent = useKindPrefs((s) => s.recent[domain])
  const togglePin = useKindPrefs((s) => s.togglePin)
  const { patch, infer } = useKindMutations()

  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const [showHidden, setShowHidden] = useState(false)
  const [confirmInfer, setConfirmInfer] = useState(false)
  const [active, setActive] = useState(0)
  const listId = `kind-picker-${domain}`
  const words = DOMAIN_WORDS[domain]

  const kinds = vocab.byDomain(domain)
  const counts = useMemo(() => kindCounts(doc, domain), [doc, domain])
  const keys = useMemo(() => new Map(digitShortcuts(vocab, domain, pins ?? []).map((d) => [d.kind.id, d.key])), [vocab, domain, pins])
  const groups = useMemo(() => groupKinds(kinds, { domain, query, pins, recent, counts }), [kinds, domain, query, pins, recent, counts])
  const hidden = useMemo(() => kinds.filter((k) => !k.enabled && !k.deprecated && matchesQuery(k, query)), [kinds, query])

  const options: Option[] = useMemo(() => {
    const out: Option[] = []
    for (const g of groups) {
      const open = !g.collapsed || expanded.has(g.id)
      if (!open) continue
      for (const k of g.kinds) out.push({ optId: `${listId}-${g.id}-${k.id.replace(/[^a-z0-9_-]/gi, '_')}`, kind: k })
    }
    return out
  }, [groups, expanded, listId])

  useEffect(() => setActive(0), [query, domain])
  const activeOpt = options[Math.min(active, options.length - 1)]

  useEffect(() => {
    if (!activeOpt) return
    document.getElementById(activeOpt.optId)?.scrollIntoView?.({ block: 'nearest' })
  }, [activeOpt])

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(options.length - 1, i + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(0, i - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (activeOpt) onPick(activeOpt.kind)
      else if (onNew && query.trim()) onNew(query.trim())
    }
  }

  const exact = kinds.some((k) => (k.label['en'] ?? '').toLowerCase() === query.trim().toLowerCase() || prettyId(k.id) === query.trim().toLowerCase())

  return (
    <div className={cn('flex min-h-0 flex-col gap-2', className)}>
      <label className="relative block">
        <span className="sr-only">Search {words.many}</span>
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
        <input
          type="search"
          value={query}
          autoFocus={autoFocus}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={`Search ${words.many}… (e.g. ${domain === 'region_kind' ? 'grass, water, wall' : domain === 'anchor_kind' ? 'bed, door, chair' : domain === 'portal_kind' ? 'door, edge' : 'walk, fly'})`}
          role="combobox"
          aria-expanded
          aria-controls={listId}
          aria-activedescendant={activeOpt?.optId}
          aria-autocomplete="list"
          className="h-11 w-full rounded-xl border border-line bg-paper pl-9 pr-3 text-sm text-ink placeholder:text-ink-3 focus-visible:focus-ring"
        />
      </label>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" data-testid="kind-picker-list">
        <ul id={listId} role="listbox" aria-label={`${words.title} to choose from`} className="flex flex-col gap-2 pb-1">
          {groups.map((g) => {
            const open = !g.collapsed || expanded.has(g.id)
            return (
              <li key={g.id} role="presentation">
                <div className="flex items-center gap-2 px-1 pb-1">
                  {g.collapsed ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-ink-3 hover:text-ink"
                      aria-expanded={open}
                      onClick={() =>
                        setExpanded((s) => {
                          const next = new Set(s)
                          if (next.has(g.id)) next.delete(g.id)
                          else next.add(g.id)
                          return next
                        })
                      }
                    >
                      {open ? <ChevronDown className="size-3.5" aria-hidden /> : <ChevronRight className="size-3.5" aria-hidden />}
                      {g.title} ({g.kinds.length})
                    </button>
                  ) : (
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-3">{g.title}</h4>
                  )}
                  {g.id === 'unset' && domain === 'region_kind' ? (
                    confirmInfer ? (
                      <span className="ml-auto flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="primary"
                          loading={infer.isPending}
                          onClick={() => infer.mutate(undefined, { onSettled: () => setConfirmInfer(false) })}
                        >
                          Yes, guess them
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmInfer(false)}>
                          Cancel
                        </Button>
                      </span>
                    ) : (
                      <Button size="sm" variant="ghost" className="ml-auto h-7 px-2 text-xs" onClick={() => setConfirmInfer(true)} title="Read each name (“floor bedside”, “river”) and give it the meaning it most likely has. Kinds you made yourself are left alone.">
                        <Wand2 aria-hidden /> Guess what these do
                      </Button>
                    )
                  ) : null}
                </div>
                {open ? (
                  <ul role="presentation" className="flex flex-col">
                    {g.kinds.map((k) => {
                      const optId = `${listId}-${g.id}-${k.id.replace(/[^a-z0-9_-]/gi, '_')}`
                      const isActive = activeOpt?.optId === optId
                      const label = kindLabel(vocab, k.id, domain)
                      const pinned = (pins ?? []).includes(k.id)
                      const key = keys.get(k.id)
                      const count = counts.get(k.id) ?? 0
                      return (
                        <li key={optId} role="presentation" className={cn('group flex items-center gap-0.5 rounded-lg', isActive ? 'bg-accent-soft' : 'hover:bg-paper-2')}>
                          <div
                            id={optId}
                            role="option"
                            aria-selected={isActive}
                            data-kind={k.id}
                            onClick={() => onPick(k)}
                            onMouseMove={() => {
                              const i = options.findIndex((o) => o.optId === optId)
                              if (i >= 0 && i !== active) setActive(i)
                            }}
                            className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 px-2 py-1.5"
                            title={describeKind(k).join(' ')}
                          >
                            <span className="size-3 shrink-0 rounded-full border border-line" style={{ background: kindColour(vocab, k.id, domain) }} aria-hidden />
                            <KindIcon name={k.icon} className="size-4 shrink-0 text-ink-2" />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm text-ink">{label}</span>
                              <span className="block truncate text-[11px] text-ink-3">{describeKind(k)[0] ?? prettyId(k.id)}</span>
                            </span>
                            {count ? <span className="shrink-0 text-[11px] tabular-nums text-ink-3" aria-label={`${count} on this picture`}>×{count}</span> : null}
                            {key ? <kbd className="shrink-0 rounded bg-paper-3 px-1 font-mono text-[10px] text-ink-2">{key}</kbd> : null}
                            {current === k.id ? <Check className="size-4 shrink-0 text-accent" aria-label="Current" /> : null}
                          </div>
                          <button
                            type="button"
                            tabIndex={-1}
                            aria-pressed={pinned}
                            aria-label={pinned ? `Unpin ${label}` : `Pin ${label} to a number key`}
                            title={pinned ? 'Unpin' : 'Pin to a number key'}
                            onClick={() => togglePin(domain, k.id)}
                            className={cn('inline-flex size-8 shrink-0 items-center justify-center rounded-md text-ink-3 hover:bg-paper-3 hover:text-ink', pinned ? 'text-accent' : 'opacity-0 group-hover:opacity-100 focus:opacity-100')}
                          >
                            {pinned ? <PinOff className="size-4" aria-hidden /> : <Pin className="size-4" aria-hidden />}
                          </button>
                          {onEdit ? (
                            <button
                              type="button"
                              tabIndex={-1}
                              aria-label={`Edit ${label}`}
                              title="Edit this kind"
                              onClick={() => onEdit(k)}
                              className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-ink-3 opacity-0 hover:bg-paper-3 hover:text-ink focus:opacity-100 group-hover:opacity-100"
                            >
                              <Pencil className="size-4" aria-hidden />
                            </button>
                          ) : null}
                        </li>
                      )
                    })}
                  </ul>
                ) : null}
              </li>
            )
          })}
          {groups.length === 0 ? (
            <li role="presentation" className="px-2 py-6 text-center text-sm text-ink-2">
              {query.trim() ? `No ${words.one} called “${query.trim()}” yet.` : `No ${words.many} in the list yet.`}
            </li>
          ) : null}
        </ul>

        {hidden.length ? (
          <div className="mt-2 border-t border-line pt-2">
            <button type="button" className="inline-flex items-center gap-1 px-1 text-xs font-semibold uppercase tracking-wide text-ink-3 hover:text-ink" aria-expanded={showHidden} onClick={() => setShowHidden((v) => !v)}>
              {showHidden ? <ChevronDown className="size-3.5" aria-hidden /> : <ChevronRight className="size-3.5" aria-hidden />}
              Hidden {words.many} ({hidden.length})
            </button>
            {showHidden ? (
              <ul className="mt-1 flex flex-col">
                {hidden.map((k) => (
                  <li key={k.id} className="flex items-center gap-2 px-2 py-1 text-sm text-ink-2">
                    <span className="size-3 shrink-0 rounded-full border border-line opacity-60" style={{ background: k.color }} aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{kindLabel(vocab, k.id, domain)}</span>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => patch.mutate({ domain, id: k.id, body: { enabled: true } })}>
                      Show again
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>

      {onNew ? (
        <Button variant={query.trim() && !exact ? 'primary' : 'outline'} size="sm" className="shrink-0 justify-start" onClick={() => onNew(query.trim())}>
          <Plus aria-hidden />
          {query.trim() && !exact ? `New ${words.one} “${query.trim()}”` : `New ${words.one}`}
        </Button>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* The dialogs                                                                 */
/* -------------------------------------------------------------------------- */

function pickerTitle(domain: StageDomain, purpose: PickPurpose): { title: string; description: string } {
  const w = DOMAIN_WORDS[domain]
  switch (purpose.kind) {
    case 'set':
      return { title: 'What is it?', description: `Pick what this ${w.one} is. Type to search, or make a new one.` }
    case 'add':
      return { title: `Add a ${w.one}`, description: `First pick what kind of ${w.one} it is.` }
    case 'convert':
      return {
        title: `Change to a ${w.one}`,
        description:
          purpose.ids.length > 1
            ? `Pick what kind of ${w.one} they become. Undo changes them back.`
            : `Pick what kind of ${w.one} it becomes. Undo changes it back.`,
      }
    default:
      return { title: `Choose a ${w.one}`, description: `The next ${w.one} you add will be this.` }
  }
}

/** Pushes the `dialog` shortcut scope while open, so editor keys (digits, Delete) stay quiet. */
export function useDialogScope(open: boolean): void {
  useEffect(() => (open ? enterScope('dialog') : undefined), [open])
}

/**
 * Where the focus goes when the picker closes. The dialog has no trigger of its own, so Radix would
 * drop it on `<body>` — and a keyboard user who changed a row from its "…" menu (whose item is gone)
 * or turned a way in/out into a spot (whose "A spot…" button is gone) would have to start again from
 * the top of the page. So: back to whatever opened the picker when it is still there, else to the
 * row of what is now selected, else to the details panel's "What is it?".
 */
function restoreFocusAfterPicker(opener: HTMLElement | null): void {
  const ui = useKindUi.getState()
  if (ui.picker || ui.editor) return // the next kind dialog (New / Edit kind) has the focus now
  if (opener && opener.isConnected && !opener.closest('[role="menu"]')) {
    opener.focus()
    return
  }
  const id = useEditorStore.getState().selection.ids[0]
  const esc = (s: string) => (typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(s) : s.replace(/["\\]/g, (c) => `\\${c}`))
  const row = id ? document.querySelector<HTMLElement>(`[data-row="${esc(id)}"]`) : null
  ;(row ?? document.querySelector<HTMLElement>('[data-testid="kind-trigger"]'))?.focus()
}

function KindPickerDialog() {
  const picker = useKindUi((s) => s.picker)
  const closePicker = useKindUi((s) => s.closePicker)
  const openNewKind = useKindUi((s) => s.openNewKind)
  const openEditKind = useKindUi((s) => s.openEditKind)
  const doc = useEditorStore((s) => s.doc)
  const open = picker !== null
  useDialogScope(open)
  if (!picker) return null
  const { domain, purpose } = picker
  const { title, description } = pickerTitle(domain, purpose)
  const current = purpose.kind === 'set' && purpose.ids.length === 1 ? featureKind(doc, purpose.ids[0]!) : undefined
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? undefined : closePicker())}>
      <DialogContent
        size="md"
        className="flex max-h-[min(85dvh,760px)] flex-col gap-3 overflow-hidden p-4 sm:p-5"
        data-testid="kind-picker-dialog"
        onCloseAutoFocus={(e) => {
          e.preventDefault()
          restoreFocusAfterPicker(pickerOpener)
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <KindPicker
          domain={domain}
          current={current}
          autoFocus
          className="min-h-0 flex-1"
          onPick={(k) => {
            closePicker()
            performPick(domain, k.id, purpose)
          }}
          onNew={(name) => openNewKind(domain, purpose, name)}
          onEdit={(k) => openEditKind(domain, k.id)}
        />
      </DialogContent>
    </Dialog>
  )
}

function featureKind(doc: ReturnType<typeof useEditorStore.getState>['doc'], id: string): string | undefined {
  const d = domainOfFeature(doc, id)
  return d ? featuresOf(doc, d).find((f) => f.id === id)?.kind : undefined
}

/**
 * The kind picker and the new/edit kind dialog, rendered once by the annotator. Everything
 * else opens them through {@link useKindUi}.
 */
export function KindDialogs() {
  const editor = useKindUi((s) => s.editor)
  const closeEditor = useKindUi((s) => s.closeEditor)
  const closePicker = useKindUi((s) => s.closePicker)
  return (
    <>
      <KindPickerDialog />
      {editor ? (
        <NewKindDialog
          open
          onOpenChange={(o) => (o ? undefined : closeEditor())}
          domain={editor.domain}
          kindId={editor.mode === 'edit' ? editor.id : undefined}
          initialName={editor.mode === 'new' ? editor.name : undefined}
          // "New …" replaced the picker, so it hands the focus on the same way the picker would.
          onCloseAutoFocus={
            editor.mode === 'new'
              ? (e) => {
                  e.preventDefault()
                  restoreFocusAfterPicker(pickerOpener)
                }
              : undefined
          }
          onDone={(kind) => {
            closeEditor()
            if (editor.mode === 'new') {
              closePicker()
              performPick(editor.domain, kind.id, editor.purpose)
            }
          }}
        />
      ) : null}
    </>
  )
}
